import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterSelectBox from '../OfferFilterSelectBox'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'

const SizedFilterSelectBox = styled(OfferFilterSelectBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  checkIn?: string;
  checkOut?: string;
  isOpen: boolean;
  inSidebar?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

function OfferDatePickerSelectBox(props: Props) {
  const {
    checkIn,
    checkOut,
    isOpen,
    inSidebar,
    ref,
  } = props

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkOut ? moment(checkOut) : undefined, [checkOut])
  const selectedDuration = useMemo(() => checkOutMoment?.diff(checkInMoment, 'days'), [checkInMoment, checkOutMoment])
  const areDatesValid = checkIn && checkOut
  const dispatch = useOfferPageDispatch()

  const onClick = useCallback(() => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
    }
  }, [dispatch, isOpen, trackEvent])

  return (
    <SizedFilterSelectBox
      onClick={onClick}
      active={isOpen}
      ref={ref}
      id="date-picker"
      data-testid="OfferDatePickerSelectBox"
      icon={<LineCalendarIcon />}
      inSidebar={inSidebar}
      label="When"
      placeholder="Select your dates"
      value={areDatesValid ? <><b>{checkInMoment?.format(DAY_MONTH_NAME)} - {checkOutMoment?.format(DAY_MONTH_NAME)}</b> ({pluralizeToString('night', selectedDuration ?? 1)})</> : undefined}
    />
  )
}

export default withOfferPageState(state => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  isOpen: state.activeFilter === 'checkin',
}), OfferDatePickerSelectBox)
